<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { memberGet } from "@/common/api/rest/member";
import {
	accountShowByMember,
	accountStatus,
} from "@/common/api/rest/account/account";

import {
	affiliateList,
	affiliatePatch,
	affiliateCreate,
	affiliateDelete,
} from "@/common/api/rest/account/affiliate";

import {
	baseInput,
	baseSelect,
	baseTextarea,
	baseConfirm,
	baseModal,
	baseTable,
	breadcrumbSimple,
	tabMenu,
} from "@/common/components";

import FormAffiliate from "@/components/forms/form-affiliate.vue";

type TabActiveType = "edit" | "cv";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const member = ref<any>(null);
const account = ref<any>(null);
const selected = ref<any>(null);
const payload = ref<any>(null);

const tableData = ref<any[]>([]);
const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const isEdit = ref(false);

const memberId = computed(() => route.params.member as string);

const tableHeader = [
	{ key: "name", label: "Nome" },
	{ key: "email", label: "Email" },
	{ key: "type", label: "Tipo" },
	{ key: "status", label: "Status" },
];

const breadcrumbList = computed(() => [
	{
		key: "members",
		label: t("common.labels.members"),
		url: "/members",
	},
	{
		key: "member",
		label: member.value?.name || t("common.labels.member"),
	},
	{
		key: "edit",
		label: t("common.labels.edit"),
	},
]);

const tabList = computed(() => [
	{
		key: "edit",
		label: t("common.labels.member").toUpperCase(),
	},
	{
		key: "account",
		label: t("common.labels.account").toUpperCase(),
	},
	{
		key: "affiliate",
		label: "AFILIADOS",
	},
]);

const statusEnum = computed(() => ({
	ACTIVE: "ATIVO",
	INACTIVE: "INATIVO",
	CANCELED: "CANCELADO",
	RESTRICTED: "RESTRITO",
	PAST_DUE: "EM ATRASO",
}));

async function fetchData() {
	await memberGet(memberId.value)
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				member.value = data;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});

	await accountShowByMember(memberId.value)
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				account.value = data;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function fecthAffiliates() {
	affiliateList({
		account_id: account.value.account_id,
		page: page.value,
		limit: limit.value,
		search: search.value,
	})
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				tableData.value = data.list;
				page.value = data.infos.page;
				pages.value = data.infos.pages;
				total.value = data.infos.total;
				searched.value = data.infos.count;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function changePage(value: number) {
	page.value = value;
	fecthAffiliates();
}

function searchItem(value: string) {
	search.value = value;
	page.value = 0;

	fecthAffiliates();
}

function openModal(data?: any) {
	if (data) {
		selected.value = data;
		isEdit.value = true;
	}

	showModalAdd.value = true;
}

function closeModal() {
	showModalAdd.value = false;
}

function updateAffiliate() {
	if (!selected.value) return;

	affiliatePatch(selected.value.affiliate_id, {
		type: payload.value.type,
		status: payload.value.status,
	})
		.then((response) => {
			const { success, message } = response;

			if (success) {
				closeModal();
				fecthAffiliates();
				toast({ message: message, type: "success" });
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function createAffiliate() {
	if (!payload.value) return;

	store.commit("SET_LOADING", true);

	affiliateCreate({
		...payload.value,
		account_id: account.value.account_id,
	})
		.then((response) => {
			const { success, message } = response;

			if (success) {
				closeModal();
				fecthAffiliates();
				toast({ message: message, type: "success" });
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function submitPayload() {
	if (isEdit.value) {
		updateAffiliate();
	} else {
		createAffiliate();
	}
}

function removeAffiliate(value: any) {
	selected.value = value;
	showModalDel.value = true;
}

function removeAffiliateCancel() {
	selected.value = null;
	showModalDel.value = false;
}

function removeAffiliateConfirm() {
	if (!selected.value) return;

	store.commit("SET_LOADING", true);

	affiliateDelete(selected.value.affiliate_id)
		.then((response) => {
			const { success, message } = response;

			fecthAffiliates();
			removeAffiliateCancel();
			toast({ message: message, type: success ? "success" : "error" });
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function selectTab(tab: TabActiveType) {
	router.push(`/members/${memberId.value}/${tab}`);
}

onMounted(async () => {
	await fetchData();

	fecthAffiliates();
});
</script>

<template>
	<article class="member-page">
		<section class="page-header">
			<breadcrumb-simple
				:items="breadcrumbList"
				:show-home="true"
				url-home="/home"
				active="edit"
			/>
		</section>

		<section class="page-content">
			<tab-menu
				:tabs="tabList"
				:tooltip="false"
				active="affiliate"
				width="fit-content"
				padding-inline="1.5rem"
				@select:tab="selectTab"
			>
				<template #content>
					<base-table
						:header="tableHeader"
						:data="tableData"
						:limit="limit"
						:page="page"
						:pages="pages"
						:total="total"
						:searched="searched"
						:options="true"
						:add="true"
						:del="true"
						:edit="true"
						@change:page="changePage($event)"
						@searching="searchItem($event)"
						@btn:add="openModal()"
						@btn:edit="openModal($event)"
						@btn:del="removeAffiliate($event)"
					>
						<template #status="{ data }">
							{{ statusEnum[data.status] }}
						</template>

						<template #name="{ data }">
							{{ data.name }}
						</template>
					</base-table>
				</template>
			</tab-menu>
		</section>

		<base-modal
			:show="showModalAdd"
			:show-button-confirm="true"
			:button-confirm="t('common.labels.save')"
			:button-close="t('common.labels.cancel')"
			@close="closeModal()"
			@confirm="submitPayload()"
		>
			<template #title>
				<div
					class="flex flex-row gap-1 items-center text-6 font-bold uppercase mb-4"
				>
					{{ isEdit ? "EDITAR" : "ADICIONAR" }} AFFILIADO
				</div>
			</template>

			<template #content>
				<form-affiliate :data="selected" @update-data="payload = $event" />
			</template>
		</base-modal>

		<base-confirm
			:show="showModalDel"
			title="Remover Affiliado"
			:message="
				t('common.texts.remove_message', {
					concat: selected?.name,
				})
			"
			@cancel="removeAffiliateCancel"
			@confirm="removeAffiliateConfirm"
		/>
	</article>
</template>

<style lang="scss" scoped>
.member-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.page-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: inherit;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1rem;
		padding-block: 0.5rem;
	}

	.page-content {
		display: flex;
		flex-direction: column;
		margin-block-end: 1.54rem;

		.member-form {
			display: flex;
			flex-direction: column;
			gap: 1rem;
		}
	}
}
</style>
